import { produce } from "immer";
import { getCellId, splitCellId } from "../../../components/process-design/cell/Utils";
import { isCellWithinRange } from "../../../utils/util";
import Content from "./Content";
import { HotkeyScopes } from "../../../utils/constants";
import MetaData from "./MetaData";
const utils = (context) => {
  const { dispatch, rootState, current } = context;
  return {
    hasMergedCells(range) {
      return Object.keys(rootState.cells.mergedCellIds).some((mergeId) => {
        const [rowId, columnId] = splitCellId(mergeId);
        const rowIndex = current.design.rowMap[rowId].index;
        const columnIndex = current.design.columnMap[columnId].index;
        return isCellWithinRange([columnIndex, rowIndex], range);
      });
    },
    getMergedCells(range) {
      const mergedCellIds = new Set();
      const mergeCellClones = {};
      Object.entries(rootState.cells.mergedCellIds).some(([mergeId, value]) => {
        const [rowId, columnId] = splitCellId(mergeId);
        const rowIndex = current.design.rowMap[rowId].index;
        const columnIndex = current.design.columnMap[columnId].index;
        if (isCellWithinRange([columnIndex, rowIndex], range)) mergedCellIds.add(value);
      });

      mergedCellIds.forEach((item) => {
        mergeCellClones[item] = { ...current.design.mergedCells[item] };
      });
      return mergeCellClones;
    },
    async updateMergedCells() {
      const { rowMap, columnMap } = current.design;

      const updatedMergedCells = produce(current.design.mergedCells, (mergedCells) => {
        Object.values(mergedCells).forEach(({ rows, columns }) => {
          const startRow = rowMap[rows.startRowId];
          const endRow = rowMap[rows.endRowId];
          const startColumn = columnMap[columns.startColumnId];
          const endColumn = columnMap[columns.endColumnId];

          const minRowIndex = Math.min(startRow.index, endRow.index);
          const maxRowIndex = Math.max(startRow.index, endRow.index);

          rows.startRowId = current.design.rows[minRowIndex].id;
          rows.endRowId = current.design.rows[maxRowIndex].id;

          const minColumnIndex = Math.min(startColumn.index, endColumn.index);
          const maxColumnIndex = Math.max(startColumn.index, endColumn.index);

          columns.startColumnId = current.design.columns[minColumnIndex].id;
          columns.endColumnId = current.design.columns[maxColumnIndex].id;

          const newMergeId = `${rows.startRowId}_${columns.startColumnId}_merged`;
          mergedCells[newMergeId] = { id: newMergeId, columns, rows }; // Update the mergedCells directly
        });
      });

      current.design.mergedCells = updatedMergedCells;
      await dispatch("persistDimensions", "mergedCells");
      this.loadMergedCellIds();
    },
    loadMergedCellIds() {
      // create an object in the store to have all the merged cells with their mergedId.
      const mergedCellIds = {};

      Object.values(current.design.mergedCells).forEach((mergedCell) => {
        const { rows, columns } = mergedCell;
        const startColumnIndex = current.design.columnMap[columns.startColumnId].index;
        const endColumnIndex = current.design.columnMap[columns.endColumnId].index;
        const startRowIndex = current.design.rowMap[rows.startRowId].index;
        const endRowIndex = current.design.rowMap[rows.endRowId].index;

        for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex++) {
          const rowId = current.design.rows[rowIndex].id;
          for (let columnIndex = startColumnIndex; columnIndex <= endColumnIndex; columnIndex++) {
            const columnId = current.design.columns[columnIndex].id;
            const cellId = `${rowId}_${columnId}`;
            mergedCellIds[cellId] = mergedCell.id;
          }
        }
      });

      rootState.cells.mergedCellIds = mergedCellIds;
    },
    async recalculateMergedCells({ columnIndexes, rowIndexes }) {
      const indexes = columnIndexes || rowIndexes;

      const updatedMergedCells = produce(current.design.mergedCells, (mergedCells) => {
        indexes.forEach((index) => {
          const columnOrRows = columnIndexes ? "columns" : "rows";
          const id = current.design[columnOrRows][index].id;

          Object.entries(mergedCells).forEach(([key, value]) => {
            const { rows, columns } = value;

            // in case it is column
            if (columnIndexes) {
              const startColumnIndex = current.design.columnMap[columns.startColumnId].index;
              const startRowIndex = current.design.rowMap[rows.startRowId].index;
              const endRowIndex = current.design.rowMap[rows.endRowId].index;

              if (id === columns.startColumnId) {
                const nextColumnId = current.design.columns[startColumnIndex + 1]?.id;

                if (
                  id === columns.endColumnId ||
                  (nextColumnId === columns.endColumnId && startRowIndex === endRowIndex)
                ) {
                  delete mergedCells[key];
                  return;
                }

                const firstCell = `${rows.startRowId}_${nextColumnId}`;
                const newMergedCellId = `${firstCell}_merged`;
                const newColumns = { ...columns, startColumnId: nextColumnId };

                mergedCells[newMergedCellId] = { ...mergedCells[key], id: newMergedCellId, columns: newColumns };
                delete mergedCells[key];
              }

              if (id === columns.endColumnId) {
                const previousColumnId = current.design.columns[indexes[0] - 1]?.id;

                if (previousColumnId === columns.startColumnId && startRowIndex === endRowIndex) {
                  delete mergedCells[key];
                  return;
                }

                const newColumns = { ...columns, endColumnId: previousColumnId };

                mergedCells[key] = { ...mergedCells[key], columns: newColumns };
              }
            }

            // if row
            if (rowIndexes) {
              const startRowIndex = current.design.rowMap[rows.startRowId].index;
              const startColumnIndex = current.design.columnMap[columns.startColumnId].index;
              const endColumnIndex = current.design.columnMap[columns.endColumnId].index;

              if (id === rows.startRowId) {
                const nextRowId = current.design.rows[startRowIndex + 1]?.id;

                if (id === rows.endRowId || (nextRowId === rows.endRowId && startColumnIndex === endColumnIndex)) {
                  delete mergedCells[key];
                  return;
                }

                const firstCell = `${nextRowId}_${columns.startColumnId}`;
                const newMergedCellId = `${firstCell}_merged`;
                const newRows = { ...rows, startRowId: nextRowId };

                mergedCells[newMergedCellId] = { ...mergedCells[key], id: newMergedCellId, rows: newRows };
                delete mergedCells[key];
              }

              if (id === rows.endRowId) {
                const previousRowId = current.design.rows[indexes[0] - 1]?.id;

                if (previousRowId === rows.startRowId && startColumnIndex === endColumnIndex) {
                  delete mergedCells[key];
                  return;
                }

                const newRows = { ...rows, endRowId: previousRowId };

                mergedCells[key] = { ...mergedCells[key], rows: newRows };
              }
            }
          });
        });
      });

      // Assign the updated merged cells to the state
      current.design.mergedCells = updatedMergedCells;
      this.loadMergedCellIds();
      await dispatch("persistDimensions", "mergedCells");
    },
  };
};

const main = (context) => {
  const { dispatch, rootState, rootGetters, currentSheetName, current } = context;
  return {
    async mergeRowGroupColumnsAfterInsert(index, count) {
      const rows = current.design.rows.slice(index, index + count);
      const mergedCells = { ...current.design.mergedCells };
      const mergeIds = [];
      const columnsToBeMerged = current.design.columns.filter(
        (item) => item.type === "player" && item.id.includes("-Plus")
      );
      rows.forEach((row) => {
        columnsToBeMerged.forEach((column) => {
          const firstCellId = `${row.id}_${column.id}`;
          const mergeId = `${firstCellId}_merged`;
          mergeIds.push(mergeId);
          mergedCells[mergeId] = {
            id: mergeId,
            columns: {
              startColumnId: column.id,
              endColumnId: column.id.replace("-Plus", "-Minus"),
            },
            rows: {
              startRowId: row.id,
              endRowId: row.id,
            },
          };
        });
      });
      current.design.mergedCells = mergedCells;
      utils(context).loadMergedCellIds();
      await dispatch("persistDimensions", "mergedCells");

      return async () => {
        current.design.mergedCells = produce(current.design.mergedCells, (mergedCells) => {
          mergeIds.forEach((mergeId) => delete mergedCells[mergeId]);
        });
        utils(context).loadMergedCellIds();
        await dispatch("persistDimensions", "mergedCells");
      };
    },
    async mergeRows(rowTypes, columnIds) {
      const rowsToBeMerged = current.design.rows.filter((row) => rowTypes.includes(row.type));
      const prevMergedCells = current.design.mergedCells;
      const mergedCells = { ...current.design.mergedCells };
      rowsToBeMerged.forEach((row) => {
        columnIds.forEach((columnId) => {
          const firstCellId = getCellId(row.id, columnId);
          const mergeId = `${firstCellId}_merged`;

          mergedCells[mergeId] = {
            id: mergeId,
            columns: {
              startColumnId: columnId,
              endColumnId: `${columnId.replace("-Plus", "-Minus")}`,
            },
            rows: {
              startRowId: row.id,
              endRowId: row.id,
            },
          };
        });
      });
      current.design.mergedCells = mergedCells;
      utils(context).loadMergedCellIds();
      await dispatch("persistDimensions", "mergedCells");

      return async () => {
        current.design.mergedCells = prevMergedCells;
        utils(context).loadMergedCellIds();

        await dispatch("persistDimensions", "mergedCells");
      };
    },
    async mergeCells(range) {
      const rows = rootGetters["cells/rowsInRange"](range);
      const columns = rootGetters["cells/columnsInRange"](range);

      // Avoid one cell merging
      if (rows.length === 1 && columns.length === 1) return () => {};

      const firstCellId = getCellId(rows[0], columns[0]);
      const mergeId = `${firstCellId}_merged`;
      const firstCellText = rootGetters["cells/firstNonEmptyCell"](range);
      current.design.mergedCells = produce(current.design.mergedCells, (mergedCells) => {
        mergedCells[mergeId] = {
          id: mergeId,
          columns: {
            startColumnId: columns[0],
            endColumnId: columns[columns.length - 1],
          },
          rows: {
            startRowId: rows[0],
            endRowId: rows[rows.length - 1],
          },
        };
      });

      const contentReverseFunc = await Content(context).main.remove(range);
      await Content(context).main.set([range[0], range[1], range[0], range[1]], firstCellText);
      const metaDataReverseFunc = MetaData(context).main.remove(range);

      dispatch("setScope", HotkeyScopes.ProcessDesigner, { root: true });

      return () => {
        this.unmergeCells(range);
        contentReverseFunc();
        metaDataReverseFunc();
      };
    },
    async unmergeCells(range) {
      const previousMergeCells = utils(context).getMergedCells(range);
      let activeCellId = rootState.cells.activeCellId;
      current.design.mergedCells = produce(current.design.mergedCells, (mergedCells) => {
        const mergedCellIds = rootGetters["cells/rangeIds"](range).filter((x) => x.endsWith("_merged"));

        // Update activeCellId if necessary
        if (mergedCellIds.includes(activeCellId)) {
          activeCellId = rootState.cells.mergedCellIds[activeCellId].replace("_merged", "");
        }

        // Remove the merged cells
        mergedCellIds.forEach((id) => {
          delete mergedCells[id];
        });
      });

      dispatch("setScope", HotkeyScopes.ProcessDesigner, { root: true });
      return async () => {
        current.design.mergedCells = { ...current.design.mergedCells, ...previousMergeCells };
        utils(context).loadMergedCellIds();
      };
    },
  };
};
export default (context) => {
  return {
    main: main(context),
    utils: utils(context),
  };
};
